import { useState, type FormEvent } from 'react'
import axios from 'axios'
import { useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { useToast } from '@/components/ui/Toast'
import { aplicacaoApi, empresaApi, perfilApi, usuarioApi } from '@/services/api'
import type { Aplicacao, Empresa, Perfil, Usuario } from '@/types/api'

export type CreateEntityKind = 'empresa' | 'usuario' | 'perfil' | 'aplicacao'
type EditableEntity = Empresa | Usuario | Perfil | Aplicacao

interface Props {
  kind: CreateEntityKind | null
  onClose: () => void
  empresaId?: string
  empresas?: Empresa[]
  entity?: EditableEntity | null
}

const labels: Record<CreateEntityKind, string> = {
  empresa: 'Empresa',
  usuario: 'Usuário',
  perfil: 'Perfil',
  aplicacao: 'Aplicação'
}

const inputClass = 'w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500'
const labelClass = 'block text-sm font-medium text-slate-300 mb-1.5'

function Field({ label, name, type = 'text', required = false, placeholder, defaultValue }: {
  label: string
  name: string
  type?: string
  required?: boolean
  placeholder?: string
  defaultValue?: string
}) {
  return <label>
    <span className={labelClass}>{label}{required && ' *'}</span>
    <input className={inputClass} name={name} type={type} required={required} placeholder={placeholder} defaultValue={defaultValue} />
  </label>
}

export function CreateEntityModal({ kind, onClose, empresaId, empresas = [], entity }: Props) {
  const [saving, setSaving] = useState(false)
  const queryClient = useQueryClient()
  const { addToast } = useToast()
  const editing = Boolean(entity)
  const value = <T extends EditableEntity, K extends keyof T>(key: K) => entity && key in entity ? String((entity as T)[key] ?? '') : ''

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!kind) return
    const values = Object.fromEntries(new FormData(event.currentTarget))
    setSaving(true)
    try {
      if (kind === 'empresa') {
        const payload = {
          nome: values.nome, nomeFantasia: values.nomeFantasia || '', cnpj: values.cnpj,
          email: values.email, telefone: values.telefone || '', idiomaPadrao: values.idiomaPadrao,
          cep: values.cep, rua: values.rua, numero: values.numero || '', complemento: values.complemento || '',
          bairro: values.bairro || '', cidade: values.cidade, estado: values.estado, pais: String(values.pais).toUpperCase()
        }
        editing ? await empresaApi.update(entity!.id, payload) : await empresaApi.create(payload)
      } else if (kind === 'usuario') {
        const payload = {
          nome: values.nome, email: values.email, empresaId: values.empresaId,
          telefone: values.telefone || null, cpf: values.cpf || null,
          idioma: values.idioma, filialId: values.filialId || null,
          ativo: entity && 'ativo' in entity ? entity.ativo : true
        }
        editing
          ? await usuarioApi.update(entity!.id, payload)
          : await usuarioApi.create({ ...payload, senha: values.senha, isSsoUser: false, ssoProvider: null, ssoExternalId: null })
      } else if (kind === 'perfil') {
        const payload = {
          empresaId: values.empresaId, nome: values.nome, descricao: values.descricao || '',
          isPadrao: values.isPadrao === 'on', ativo: entity && 'ativo' in entity ? entity.ativo : true
        }
        editing ? await perfilApi.update(entity!.id, payload) : await perfilApi.create(payload)
      } else {
        const payload = {
          nome: values.nome, descricao: values.descricao || '', codigo: String(values.codigo).toUpperCase(),
          url: values.url, icone: values.icone || null, cor: values.cor || null,
          isPublica: values.isPublica === 'on', ativa: entity && 'ativa' in entity ? entity.ativa : true,
          ssoClientId: null, ssoAuthority: null, ssoScopes: null
        }
        editing ? await aplicacaoApi.update(entity!.id, payload) : await aplicacaoApi.create(payload)
      }
      await queryClient.invalidateQueries({ queryKey: [kind === 'empresa' ? 'empresas' : kind === 'usuario' ? 'usuarios' : kind === 'perfil' ? 'perfis' : 'aplicacoes'] })
      await queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] })
      addToast({ type: 'success', title: `${labels[kind]} ${editing ? 'atualizado' : 'cadastrado'} com sucesso` })
      onClose()
    } catch (error) {
      const description = axios.isAxiosError(error)
        ? (error.response?.data?.detail || error.response?.data?.message || 'Verifique os dados informados.')
        : 'Não foi possível concluir a operação.'
      addToast({ type: 'error', title: editing ? 'Erro ao atualizar' : 'Erro ao cadastrar', description: String(description) })
    } finally {
      setSaving(false)
    }
  }

  if (!kind) return null
  const user = kind === 'usuario' && entity ? entity as Usuario : null
  const profile = kind === 'perfil' && entity ? entity as Perfil : null
  const application = kind === 'aplicacao' && entity ? entity as Aplicacao : null

  return <Modal isOpen onClose={onClose} title={`${editing ? 'Editar' : kind === 'empresa' ? 'Nova' : 'Novo'} ${labels[kind]}`} size={kind === 'empresa' ? 'lg' : 'md'}>
    <form onSubmit={submit} className="space-y-4">
      {kind === 'usuario' && <div className="space-y-4">
        <Field label="Nome" name="nome" required defaultValue={user?.nome} />
        <Field label="Email" name="email" type="email" required defaultValue={user?.email} />
        <label>
          <span className={labelClass}>Empresa *</span>
          <select className={inputClass} name="empresaId" required defaultValue={user?.empresaId || empresaId || ''}>
            <option value="" disabled>Selecione a empresa</option>
            {empresas.filter((empresa) => empresa.ativa).map((empresa) => (
              <option key={empresa.id} value={empresa.id}>{empresa.nomeFantasia || empresa.nome}</option>
            ))}
          </select>
        </label>
        <Field label="Telefone" name="telefone" defaultValue={user?.telefone} />
        <Field label="CPF" name="cpf" defaultValue={user?.cpf} />
        <Field label="Filial" name="filialId" defaultValue={user?.filialId} />
        <label><span className={labelClass}>Idioma *</span><select className={inputClass} name="idioma" defaultValue={user?.idioma || 'pt-BR'}><option value="pt-BR">Português</option><option value="en-US">English</option><option value="es-ES">Español</option></select></label>
        {!editing && <Field label="Senha temporária" name="senha" type="password" required placeholder="Mínimo 12 caracteres" />}
      </div>}
      {kind === 'perfil' && <div className="space-y-4">
        {empresas.length > 0 ? (
          <label>
            <span className={labelClass}>Empresa *</span>
            <select className={inputClass} name="empresaId" required defaultValue={profile?.empresaId || empresaId || ''}>
              <option value="" disabled>Selecione a empresa</option>
              {empresas.filter((empresa) => empresa.ativa).map((empresa) => (
                <option key={empresa.id} value={empresa.id}>{empresa.nomeFantasia || empresa.nome}</option>
              ))}
            </select>
          </label>
        ) : (
          <>
            <input type="hidden" name="empresaId" value={profile?.empresaId || empresaId || ''} />
            <p className="text-sm text-slate-400">Empresa vinculada: {profile?.empresaId || empresaId}</p>
          </>
        )}
        <Field label="Nome" name="nome" required defaultValue={profile?.nome} />
        <label><span className={labelClass}>Descrição</span><textarea className={inputClass} name="descricao" rows={3} defaultValue={profile?.descricao} /></label>
        <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" name="isPadrao" defaultChecked={profile?.isPadrao} /> Perfil padrão</label>
      </div>}
      {kind === 'aplicacao' && <div className="space-y-4">
        <Field label="Nome" name="nome" required defaultValue={application?.nome} />
        <label><span className={labelClass}>Descrição</span><textarea className={inputClass} name="descricao" rows={3} defaultValue={application?.descricao} /></label>
        <Field label="Código" name="codigo" required defaultValue={application?.codigo} />
        <Field label="URL" name="url" type="url" required defaultValue={application?.url} />
        <Field label="Ícone" name="icone" defaultValue={application?.icone} />
        <Field label="Cor" name="cor" defaultValue={application?.cor} />
        <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" name="isPublica" defaultChecked={application?.isPublica} /> Aplicação pública</label>
      </div>}
      {kind === 'empresa' && <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Field label="Razão social" name="nome" required defaultValue={value<Empresa, 'nome'>('nome')} />
        <Field label="Nome fantasia" name="nomeFantasia" defaultValue={value<Empresa, 'nomeFantasia'>('nomeFantasia')} />
        <Field label="Identificador fiscal (CNPJ ou EIN)" name="cnpj" required defaultValue={value<Empresa, 'cnpj'>('cnpj')} />
        <Field label="Email" name="email" type="email" required defaultValue={value<Empresa, 'email'>('email')} />
        <Field label="Telefone" name="telefone" defaultValue={value<Empresa, 'telefone'>('telefone')} />
        <label><span className={labelClass}>Idioma *</span><select className={inputClass} name="idiomaPadrao" defaultValue={value<Empresa, 'idiomaPadrao'>('idiomaPadrao') || 'pt-BR'}><option value="pt-BR">Português</option><option value="en-US">English</option><option value="es-ES">Español</option></select></label>
        <Field label="CEP/ZIP Code" name="cep" required defaultValue={value<Empresa, 'cep'>('cep')} />
        <Field label="Rua" name="rua" required defaultValue={value<Empresa, 'rua'>('rua')} />
        <Field label="Número" name="numero" defaultValue={value<Empresa, 'numero'>('numero')} />
        <Field label="Complemento" name="complemento" defaultValue={value<Empresa, 'complemento'>('complemento')} />
        <Field label="Bairro" name="bairro" defaultValue={value<Empresa, 'bairro'>('bairro')} />
        <Field label="Cidade" name="cidade" required defaultValue={value<Empresa, 'cidade'>('cidade')} />
        <Field label="Estado" name="estado" required defaultValue={value<Empresa, 'estado'>('estado')} />
        <Field label="País" name="pais" required defaultValue={value<Empresa, 'pais'>('pais') || 'US'} />
      </div>}
      <div className="flex justify-end gap-3 border-t border-slate-800 pt-4">
        <button type="button" onClick={onClose} disabled={saving} className="rounded-lg bg-slate-800 px-4 py-2.5 text-slate-300">Cancelar</button>
        <button type="submit" disabled={saving || (kind === 'perfil' && empresas.length === 0 && !profile?.empresaId && !empresaId) || (kind === 'usuario' && empresas.length === 0)} className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-white disabled:opacity-50">
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Salvar
        </button>
      </div>
    </form>
  </Modal>
}
